import { useLocation, useNavigate } from 'react-router';
import { VIEW_LABELS, type View } from '@mail/shared';
import { fmtWhen } from '../lib/format.ts';
import { useState } from 'react';
import { api } from '../lib/api.ts';
import {
  applyUpdate,
  loadList,
  removeThread,
  setView,
  toggleSideMini,
  useStore,
} from '../lib/store.ts';
import { suchPfad } from '../lib/suche.ts';
import { toast } from './Toast.tsx';
import { IC } from './Icons.tsx';

const NAV: Array<[View, string]> = [
  ['inbox', '📥'],
  ['mine', '👤'],
  ['pinned', '📌'],
  ['waiting', '⌛'],
  ['info', '🔔'],
  ['junk', '🚫'],
  ['later', '⏰'],
  ['done', '✓'],
  ['all', '🗄'],
];

export function Sidebar({ onCompose, onBob }: { onCompose: () => void; onBob: () => void }) {
  const nav = useNavigate();
  const loc = useLocation();
  const flowTab = loc.pathname.startsWith('/flow');
  const { view, mailbox, mailboxes, counts, sideMini, live, lastSync, me, updateReady } = useStore(
    (s) => ({
      view: s.view,
      mailbox: s.mailbox,
      mailboxes: s.mailboxes,
      counts: s.counts,
      sideMini: s.sideMini,
      live: s.live,
      lastSync: s.lastSync,
      updateReady: s.updateReady,
      me: s.me,
    }),
  );
  // Drag-and-Drop aus der Liste: Thread auf Junk / Info & System / Posteingang ziehen. Merkt sich den
  // Absender (wie „Immer aussortieren“ bzw. „Doch in den Posteingang“), mit Rückgängig.
  const [dropZiel, setDropZiel] = useState<string | null>(null);
  const abgelegt = async (id: number, ziel: View) => {
    const kategorie = ziel === 'inbox' ? 'wichtig' : ziel === 'info' ? 'info' : 'junk';
    try {
      await api.category(id, kategorie, 'sender');
      removeThread(id);
      toast(
        ziel === 'inbox'
          ? 'In den Posteingang, Absender gemerkt'
          : `Nach ${VIEW_LABELS[ziel]}, Absender gemerkt`,
        {
          undo: () =>
            api
              .category(id, ziel === 'inbox' ? 'info' : 'wichtig', 'sender')
              .then(() => loadList()),
        },
      );
    } catch {
      toast('Verschieben nicht möglich');
    }
  };
  // Postfächer, deren Sync steht (Anmeldung gestört, Server weg): sichtbar unten und am Postfach, nicht nur als
  // Tooltip – am 31.10.2026 standen sechs Postfächer zwei Tage still, ohne dass es jemand sah.
  const gestoert = mailboxes.filter((b) => b.syncEnabled && b.syncError);
  const gestoertText = gestoert.map((b) => `${b.address}: ${b.syncError}`).join('\n');
  const go = (v: View, mb: number | null = null) => {
    setView(v, mb);
    void nav('/');
  };
  return (
    <aside className="side" aria-label="Postfächer">
      <div className={`side-tabs${flowTab ? ' flow' : ''}`} role="tablist" aria-label="Bereich">
        <span className="thumb" aria-hidden="true" />
        <button
          role="tab"
          aria-selected={!flowTab}
          className={flowTab ? '' : 'on'}
          onClick={() => go('inbox')}
          title="Mail"
        >
          <span className="ico">✉️</span>
          <span className="lbl">Mail</span>
        </button>
        <button
          role="tab"
          aria-selected={flowTab}
          className={flowTab ? 'on' : ''}
          onClick={() => void nav('/flow')}
          title="Flow"
        >
          <span className="ico">📅</span>
          <span className="lbl">Flow</span>
        </button>
      </div>
      <button className="compose" onClick={onBob} title="Bob fragen (⌘K)">
        <img className="bob-ico" src="/bob-kopf.png" alt="" />
        <span className="lbl"> Bob fragen</span>
      </button>
      <button className="compose plain" onClick={onCompose} title="Neue Mail (C)">
        {IC.compose}
        <span className="lbl">Verfassen</span>
      </button>
      <div>
        <div className="sec-h">Alle Konten</div>
        <nav className="nav">
          {NAV.map(([v, ico]) => {
            const n = counts?.views[v] ?? 0;
            const ablage = v === 'inbox' || v === 'info' || v === 'junk';
            return (
              <button
                key={v}
                className={`${view === v && !mailbox ? 'on' : ''}${dropZiel === v ? ' drop-over' : ''}`}
                onClick={() => go(v)}
                title={VIEW_LABELS[v]}
                onDragOver={(e) => {
                  if (!ablage || !e.dataTransfer.types.includes('application/x-mm-thread')) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dropZiel !== v) setDropZiel(v);
                }}
                onDragLeave={() => dropZiel === v && setDropZiel(null)}
                onDrop={(e) => {
                  setDropZiel(null);
                  const id = Number(e.dataTransfer.getData('application/x-mm-thread'));
                  if (!ablage || !id) return;
                  e.preventDefault();
                  void abgelegt(id, v);
                }}
              >
                <span className="ico" aria-hidden="true">
                  {ico}
                </span>
                <span className="lbl">{VIEW_LABELS[v]}</span>
                <span className="ct">{n || ''}</span>
              </button>
            );
          })}
        </nav>
      </div>
      <div>
        <div className="sec-h">Postfächer</div>
        <nav className="nav">
          {mailboxes.map((b) => {
            const firm =
              b.visibility === 'personal'
                ? 'persönlich'
                : b.company === 'hoerthin'
                  ? 'Hört Hin!'
                  : 'DBDM';
            const n = counts?.mailboxes[String(b.id)]?.unread ?? 0;
            return (
              <button
                key={b.id}
                className={mailbox === b.id ? 'on' : ''}
                // Im Archiv bleibt man im Archiv (alle Mails dieses Kontos), sonst Posteingang des Kontos
                onClick={() => go(view === 'all' ? 'all' : 'inbox', b.id)}
                title={`${b.address}${b.syncError ? ' — ' + b.syncError : ''}`}
              >
                <span className={`dot c-${b.color}`} />
                <span className="mbname">{b.address}</span>
                <span className={`firm${b.syncError ? ' warn' : ''}`}>
                  {b.syncError ? '⚠ Sync' : b.syncEnabled ? firm : 'aus'}
                </span>
                <span className="ct">{n || ''}</span>
              </button>
            );
          })}
        </nav>
      </div>
      <div className="side-foot">
        <button className="side-search" onClick={() => void nav(suchPfad())} title="Suche (⌘F)">
          <span className="ico">{IC.search}</span>
          <span className="lbl">Suche</span>
          <kbd>⌘F</kbd>
        </button>
        <button
          className="side-search"
          onClick={() => void nav('/einstellungen')}
          title="Einstellungen"
        >
          <span className="ico">{IC.settings}</span>
          <span className="lbl">Einstellungen</span>
        </button>
        <div className="side-meta">
          {gestoert.length > 0 && (
            <span className="ico warn" title={gestoertText} aria-label="Sync gestört">
              ⚠
            </span>
          )}
          <span
            className={`lbl${gestoert.length > 0 ? ' warn' : ''}`}
            title={gestoert.length > 0 ? gestoertText : me ? `Angemeldet als ${me.name}` : ''}
          >
            {gestoert.length > 0
              ? `⚠ Sync gestört: ${gestoert.map((b) => b.address).join(', ')}`
              : live === 'verbunden'
                ? 'Live'
                : 'Verbindung getrennt'}
            {lastSync ? ` · synchronisiert ${fmtWhen(lastSync)}` : ''}
            {import.meta.env['VITE_BUILD'] ? ` · ${String(import.meta.env['VITE_BUILD'])}` : ''}
          </span>
          {updateReady && (
            <button
              className="btn update"
              onClick={applyUpdate}
              title="Eine neue Version der App ist da – neu laden"
            >
              <span className="ico">⟳</span>
              <span className="lbl">Neue Version · neu laden</span>
            </button>
          )}
          <button
            className="side-tog"
            onClick={toggleSideMini}
            aria-label="Seitenleiste ein- oder ausklappen"
            title="Seitenleiste ein-/ausklappen"
          >
            {sideMini ? '»' : '«'}
          </button>
        </div>
      </div>
    </aside>
  );
}
